import { QuizTopicPageTemplate } from "@/components/QuizTopicPageTemplate";

const tocItems = [
  { id: "why-pop-culture", title: "Why Pop Culture Quizzes?" },
  { id: "movies-tv", title: "Movies & TV Quizzes" },
  { id: "90s-nostalgia", title: "90s Nostalgia Quizzes" },
  { id: "social-media", title: "Social Media & Trends" },
];

const quizResources = [
  { name: "Pop Culture Quiz", url: "https://www.britannica.com/quiz/pop-culture-quiz", description: "Pop culture facts quiz" },
  { name: "That '90s Quiz", url: "https://www.britannica.com/quiz/that-90s-quiz", description: "90s nostalgia test" },
  { name: "Friends Trivia", url: "https://www.seventeen.com/celebrity/celeb-quizzes/a25048028/friends-quiz-trivia/", description: "Ultimate Friends fan quiz" },
  { name: "Ultimate Disney Quiz", url: "https://play.howstuffworks.com/quiz/the-ultimate-disney-quiz-1", description: "Disney movie trivia" },
  { name: "Horror Movie Quiz", url: "https://play.howstuffworks.com/quiz/the-ultimate-horror-movie-quiz", description: "Scary movie knowledge" },
];

const relatedTopics = [
  { name: "Music Quizzes", path: "/quizzes/music-quizzes/", description: "Songs and artists" },
  { name: "Video Game Quizzes", path: "/quizzes/video-game-quizzes/", description: "Gaming knowledge" },
  { name: "Fun Quizzes", path: "/quizzes/fun-quizzes-to-take/", description: "Entertainment quizzes" },
];

const PopCultureQuizzes = () => {
  return (
    <QuizTopicPageTemplate
      title="Pop Culture Quizzes: Test Your Entertainment Knowledge"
      metaTitle="Pop Culture Quizzes | Movies, TV & Entertainment Trivia 2025"
      metaDescription="Challenge your pop culture knowledge with quizzes on movies, TV shows, celebrities, and trends. From Friends trivia to 90s nostalgia - prove you're pop culture savvy."
      keywords="pop culture quizzes, movie trivia, tv show quizzes, celebrity quiz, 90s quiz, entertainment trivia, friends quiz"
      canonicalUrl="/quizzes/pop-culture-quizzes/"
      category="Entertainment"
      publishDate="January 12, 2025"
      readTime="5 min read"
      tocItems={tocItems}
      leadParagraph="Pop culture shapes how we talk, dress, and see the world. From iconic movies and TV shows to viral moments and celebrity gossip—pop culture quizzes test how tuned in you are to the entertainment zeitgeist."
      quizResources={quizResources}
      relatedTopics={relatedTopics}
    >
      <section id="why-pop-culture" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Why <span className="text-gradient">Pop Culture Quizzes?</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Pop culture is everywhere—in our conversations, memes, and daily references. Knowing pop culture helps you connect with others and understand the cultural moments that define generations.
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Stay culturally connected:</strong> Understand references and trends</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Test your TV/movie knowledge:</strong> From classics to new releases</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Relive nostalgia:</strong> Remember the hits from your era</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Fun party games:</strong> Great for trivia nights with friends</span>
          </li>
        </ul>
      </section>

      <section id="movies-tv" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Movies & TV <span className="text-gradient">Quizzes</span>
        </h2>
        
        <h3 className="text-2xl font-display font-semibold mt-8 mb-4">Friends Trivia</h3>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          "The One with the Quiz" — Friends remains one of the most beloved sitcoms ever. Can you pass <a href="https://www.seventeen.com/celebrity/celeb-quizzes/a25048028/friends-quiz-trivia/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">the ultimate Friends trivia quiz</a>? Do you remember what job Chandler has? When is Rachel's birthday?
        </p>

        <h3 className="text-2xl font-display font-semibold mt-8 mb-4">Movie Quizzes</h3>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          From Disney classics to horror films, movie quizzes cover every genre:
        </p>
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <a href="https://play.howstuffworks.com/quiz/the-ultimate-disney-quiz-1" target="_blank" rel="noopener noreferrer" className="p-4 rounded-xl bg-card/50 border border-border/50 hover:border-primary/50 transition-all group">
            <h4 className="font-semibold mb-2 group-hover:text-primary">Disney & Pixar</h4>
            <p className="text-sm text-muted-foreground">Animated classics and new favorites</p>
          </a>
          <a href="https://play.howstuffworks.com/quiz/the-ultimate-horror-movie-quiz" target="_blank" rel="noopener noreferrer" className="p-4 rounded-xl bg-card/50 border border-border/50 hover:border-primary/50 transition-all group">
            <h4 className="font-semibold mb-2 group-hover:text-primary">Horror Movies</h4>
            <p className="text-sm text-muted-foreground">Scary movie trivia</p>
          </a>
          <a href="https://play.howstuffworks.com/quiz/can-you-name-the-movie-only-one-clue" target="_blank" rel="noopener noreferrer" className="p-4 rounded-xl bg-card/50 border border-border/50 hover:border-primary/50 transition-all group">
            <h4 className="font-semibold mb-2 group-hover:text-primary">Movie Quotes</h4>
            <p className="text-sm text-muted-foreground">Name the film from one clue</p>
          </a>
          <a href="https://play.howstuffworks.com/quiz/pixar-movie-quiz" target="_blank" rel="noopener noreferrer" className="p-4 rounded-xl bg-card/50 border border-border/50 hover:border-primary/50 transition-all group">
            <h4 className="font-semibold mb-2 group-hover:text-primary">Pixar Movies</h4>
            <p className="text-sm text-muted-foreground">To Infinity and Beyond</p>
          </a>
        </div>
      </section>

      <section id="90s-nostalgia" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          90s Nostalgia <span className="text-gradient">Quizzes</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Britannica's <a href="https://www.britannica.com/quiz/that-90s-quiz" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">That '90s Quiz</a> tests your knowledge of the decade that gave us grunge, boy bands, and the rise of the internet. Do you remember:
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span>Which toys were the must-haves?</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span>What TV shows dominated the ratings?</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span>Which fashion trends defined the era?</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span>What songs topped the charts?</span>
          </li>
        </ul>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          The <a href="https://quizly.co/mandela-effect-memory-test/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Mandela Effect Memory Test</a> is another popular quiz that tests whether you remember pop culture details correctly—or if your memory has been playing tricks on you.
        </p>
      </section>

      <section id="social-media" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Social Media & <span className="text-gradient">Trends</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Pop culture vocabulary is constantly evolving. Britannica's <a href="https://www.britannica.com/quiz/pop-culture-vocabulary-quiz" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Pop Culture Vocabulary Quiz</a> tests whether you know terms like:
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Paparazzi:</strong> Where does this word come from?</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Going viral:</strong> When did this become a thing?</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Meme culture:</strong> The language of the internet</span>
          </li>
        </ul>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          ProProfs' <a href="https://www.proprofs.com/quiz-school/story.php?title=3dq-can-we-guess-which-social-media-you-mostly-visit" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Can We Guess Which Social Media You Mostly Visit?</a> takes a different approach—using your preferences to predict your social media habits.
        </p>
      </section>
    </QuizTopicPageTemplate>
  );
};

export default PopCultureQuizzes;
